import { isObject, path } from '@uswitch/ev-utils/obj'

/**
 * The type of the value, either an absolute value 'value', or a reference value 'valueFrom'.
 *
 * @typedef {string|null} ValueType
 */

/**
 * The value, either an absolute value, or a reference value.
 *
 * @typedef {*} Value
 */

/**
 * Takes an object slice from the schema, and finds whether the slice is a 'value' or 'valueFrom'
 * type.
 *
 * @param {Object} slice
 * @param {*] [slice.value] - The absolute value.
 * @param {string} [slice.valueFrom] - The path to the value on the dataset.
 * @returns {[ValueType, Value]}
 */
export const getValueType = slice => {
  if (!isObject(slice)) {
    return [null, null]
  }

  if ({}.hasOwnProperty.call(slice, 'valueFrom')) {
    return ['valueFrom', slice.valueFrom]
  }

  if ({}.hasOwnProperty.call(slice, 'value')) {
    return ['value', slice.value]
  }

  if ({}.hasOwnProperty.call(slice, 'valueCalculated')) {
    return ['valueCalculated', slice.valueCalculated]
  }

  if ({}.hasOwnProperty.call(slice, 'compound')) {
    return ['compound', JSON.stringify(slice.compound)]
  }

  return [null, null]
}

/**
 * Takes an object slice from the schema, along with the dataset, and returns the absolute value,
 * either from the dataset or the schema.
 *
 * @param {Object} slice
 * @param {*] [slice.value] - The absolute value.
 * @param {string} [slice.valueFrom] - The path to the value on the dataset.
 * @param {Object} dataset - The dataset to lookup.
 * @returns {[Value, ValueType]}
 */
export const getValue = (slice, dataset) => {
  const [type, value] = getValueType(slice)
  if (type === 'valueFrom') {
    return path(value.split('.'), dataset, null)
  }

  return value
}

export const getField = slice => getValueType(slice)[1]

export const getDisabledValue = (slice, dataset) => {
  if (!slice) {
    return false
  }

  return !getValue(slice, dataset)
}
